import React, { useEffect, useRef } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from 'reactstrap';
import { withFormik } from 'formik';
import * as Yup from 'yup';

// Matches the API's limit (the varchar(255) columns)
export const MAX_CAPABILITY_TEXT_LENGTH = 255;

const FIELD_LABELS = { name: 'Name', description: 'Description' };

function BaseCapabilityForm({
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  submitCount,
  status,
  submitText = 'Save Changes',
  cancelText = 'Cancel',
  onCancel = () => {},
  autoFocusName = false,
  extraActions = null,
}) {
  const nameRef = useRef(null);

  // Focus after the modal finishes opening, which otherwise takes focus itself
  useEffect(() => {
    if (!autoFocusName) return;
    const timeout = setTimeout(() => nameRef.current?.focus(), 0);
    return () => clearTimeout(timeout);
  }, [autoFocusName]);

  // When the form stays open after a submit, get ready for the next entry
  useEffect(() => {
    if (autoFocusName && submitCount > 0 && !isSubmitting) {
      nameRef.current?.focus();
    }
  }, [autoFocusName, submitCount, isSubmitting]);

  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-12">
          <FormGroup>
            <Label for="name" className="label-sm">
              Name
            </Label>
            <Input
              type="text"
              innerRef={nameRef}
              {...formikAttributes('name')}
            />
            {formikFeedback('name')}
          </FormGroup>
        </div>
      </div>

      <FormGroup>
        <Label for="description" className="label-sm">
          Description
        </Label>
        <Input type="textarea" {...formikAttributes('description')} />
        {formikFeedback('description')}
      </FormGroup>

      {status?.error && (
        <div className="text-danger mb-2" role="alert">
          {status.error}
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center">
        <Button type="submit" color="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : submitText}
        </Button>
        <Button
          type="cancel"
          color="secondary"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {cancelText}
        </Button>
        {extraActions}
      </div>
    </Form>
  );
}

const CapabilityForm = withFormik({
  mapPropsToValues: ({ capability }) => ({
    name: capability?.name || '',
    description: capability?.description || '',
  }),

  // Validating on blur would flag the (autofocused) empty name as soon as the
  // user reaches for Cancel, shifting the button out from under the click
  validateOnBlur: false,

  validationSchema: Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .max(
        MAX_CAPABILITY_TEXT_LENGTH,
        `Name must be ${MAX_CAPABILITY_TEXT_LENGTH} characters or fewer`
      ),
    description: Yup.string().max(
      MAX_CAPABILITY_TEXT_LENGTH,
      `Description must be ${MAX_CAPABILITY_TEXT_LENGTH} characters or fewer`
    ),
  }),

  // onSubmit may resolve to { resetForm: true } to clear the form for another entry
  handleSubmit: async (
    values,
    { props, setSubmitting, resetForm, setErrors, setTouched, setStatus }
  ) => {
    const { onSubmit = () => {} } = props;
    setSubmitting(true);
    setStatus(null);

    try {
      const result = await onSubmit(values);
      if (result?.resetForm) {
        resetForm();
      }
    } catch (error) {
      // Show the API's validation errors beside their fields
      const fieldErrors = Object.entries(error?.response?.data?.errors ?? {})
        .filter(([field]) => field in FIELD_LABELS)
        .map(([field, messages]) => [
          field,
          `${FIELD_LABELS[field]} ${[].concat(messages)[0]}`,
        ]);

      if (fieldErrors.length > 0) {
        setErrors(Object.fromEntries(fieldErrors));
        setTouched(
          Object.fromEntries(fieldErrors.map(([field]) => [field, true])),
          false
        );
      } else {
        setStatus({
          error: 'The capability could not be saved. Please try again.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  },
})(BaseCapabilityForm);

export default CapabilityForm;
